import React, { useState } from 'react';
// @ts-ignore
import styles from './DiscourseHistory.module.scss';
// @ts-ignore
import Tooltip from '../../../../Components/Popup/Tooltip';
import { useAppContext } from '../../../../Services/AppContext';
import { FormattedDateTimeWithSeconds } from '../../../../Components/FormattedDate';
import { joinClasses } from '../../../../Utils/className.util';
import FirstUpper from '../../../../Components/FirstUpper';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import useTranslation from '../../../../Hooks/useTranslation';
import Text from '../../../../Components/Base/Text/Text';
import DiscourseHistorySubItem from './DiscourseHistorySubItem';
import { useDiscourseContext } from '../../Services/DiscourseContext';
import { PreviewFile } from '../../../../Components/MediaViewer/MediaViewer';
import AntivirusCheck from '../../../../Components/Base/Loading/AntivirusCheck';
import { nl2br } from '../../../../Helpers/nl2br.helper';

interface DiscourseHistoryItemProps {
	item: {
		id: string;
		createdAt: Date;
		createdBy: {
			profile: {
				fullName: string;
			};
		};
		items: DiscourseSubItemType[];
		file: {
			avResultText: string;
		};
	};
	handleEdit: (itemId: any) => void;
	key: string;
	patientId: string;
	onShowInCalendar?: any;
}

export interface DiscourseItemExtra {
	id?: string;
	text?: string;
	title?: string;
	sentToPatient?: boolean;
	subType?: string;
}

export interface DiscourseSubItemType {
	id: string;
	type: string;
	extra: DiscourseItemExtra;
	onShowInCalendar?: any;
}

const DiscourseHistoryItem: React.FC<DiscourseHistoryItemProps> = ({
	item,
	handleEdit,
	patientId,
	onShowInCalendar,
}) => {
	const { activeDiscourseId } = useDiscourseContext();
	const { socketUrl, isLatam } = useAppContext();
	const [mediaViewerOpened, setMediaViewerOpened] = useState(false);
	const { t } = useTranslation();

	const fileToViewer: PreviewFile = {
		id: item.id,
		displayName: t('base.dekurz') ?? '',
		type: 'pdf',
		preview: socketUrl + '/document/' + item.id + '/pdf/download',
		download: socketUrl + '/document/' + item.id + '/pdf/download',
	};

	return (
		<div
			className={joinClasses([
				styles.item,
				item.id === activeDiscourseId && styles.editActive,
			])}>
			<div className={styles.header}>
				<div className={styles.date}>
					<FormattedDateTimeWithSeconds datetime={item.createdAt} />
				</div>
				<div className={styles.doctorsName}>
					<span>
						<FirstUpper>{t('base.wrote')}</FirstUpper>:
					</span>{' '}
					{item.createdBy?.profile?.fullName}
				</div>
			</div>

			<div className={styles.content}>
				<div className={styles.text}>
					{item.items
						.filter(
							subItem =>
								subItem.type === 'diagnosis' &&
								subItem?.extra?.subType === 'main_diagnosis',
						)
						.map((subItem: any, index) => {
							return (
								<div key={index} className={styles.diagnose}>
									<span>
										{t('component.discourse.mainDiagnosis')}
										{': '}
									</span>
									{subItem.extra?.text?.label}
								</div>
							);
						})}
					{item.items
						.filter(
							subItem =>
								subItem.type === 'diagnosis' &&
								subItem?.extra?.subType === 'secondary_diagnosis',
						)
						.map((subItem: any, index) => {
							return (
								<div key={index} className={styles.diagnose}>
									<span>
										{t('component.discourse.secondaryDiagnosis')}
										{': '}
									</span>
									{subItem.extra?.text?.label}
								</div>
							);
						})}
					{item.items
						.filter(subItem => subItem.type === 'content')
						.map((subItem, index) => {
							return (
								<div key={index} className={styles.mainContent}>
									{nl2br(subItem.extra?.text)}
								</div>
							);
						})}
				</div>
				{item?.file ? (
					<div className={styles.controls}>
						{item?.file?.avResultText === 'clean' ? (
							<>
								{!isLatam && (
									<Tooltip
										trigger={
											<div
												className={styles.print}
												onClick={() => setMediaViewerOpened(true)}
											/>
										}
										position='top center'>
										<div>{t('base.print')}</div>
									</Tooltip>
								)}
								<Tooltip
									trigger={
										<div
											className={styles.edit}
											onClick={() => handleEdit(item.id)}
										/>
									}
									position='top center'>
									<Text tx='base.shared.edit' firstUpper />
								</Tooltip>
							</>
						) : (
							<div>
								<AntivirusCheck inRow={true} />
							</div>
						)}
					</div>
				) : (
					<div className={styles.controls}>
						<Tooltip
							trigger={
								<div
									className={styles.edit}
									onClick={() => handleEdit(item.id)}
								/>
							}
							position='top center'>
							<Text tx='base.shared.edit' firstUpper />
						</Tooltip>
					</div>
				)}
			</div>
			<div className={styles.attachment}>
				{item.items.map(subItem => {
					switch (subItem.type) {
						case 'content':
						case 'diagnosis':
						case 'secondaryDiagnosis':
						case 'fileId':
						case 'case':
							break;
						default:
							return (
								<DiscourseHistorySubItem
									item={subItem}
									key={subItem.id}
									patientId={patientId}
									onShowInCalendar={onShowInCalendar}
								/>
							);
					}
				})}
			</div>
			<MediaViewerModal
				opened={mediaViewerOpened}
				onClose={() => setMediaViewerOpened(false)}
				file={fileToViewer}
			/>
		</div>
	);
};

export default DiscourseHistoryItem;
